import Vue from 'vue';
import { handleDataInvoiceChange } from '@/utils/invoice';
import DateTimeUtils from '@/utils/datetime';
import BaseProxy from './base-proxy';

function roundedInvoiceAmount(inv) {
  if (inv.total_amount_with_tax) {
    // eslint-disable-next-line radix
    inv.total_amount_with_tax = parseInt(inv.total_amount_with_tax).toString();
  }
  if (inv.total_amount_without_tax) {
    // eslint-disable-next-line radix
    inv.total_amount_without_tax = parseInt(inv.total_amount_without_tax).toString();
  }
  return inv;
}

function roundedAmountDecorator(res) {
  res.invoices.forEach(roundedInvoiceAmount);
  return res;
}

/**
 * Class InvoiceProxy
 */
class InvoiceProxy extends BaseProxy {
  /**
   * Constructor.
   *
   * @param {Object} parameters The query parameters.
   */
  constructor(parameters = {}) {
    super('/invoice-robotics-api/api', parameters);
  }

  searchInvoices(searchText) {
    const promise = this.submit(
      'get',
      `${this.endpoint}/search-invoices?limit=100&offset=0&search_text=${encodeURIComponent(searchText)}`,
    );
    return new Promise((resolve, reject) => {
      promise.then(
        (res) => {
          resolve(roundedAmountDecorator(res));
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  getInvoices(options = {}) {
    return this.submit('get', `${this.endpoint}/v2/invoices`, null, { showResponseStatus: true, ...options });
  }

  getInvoicesReleasing(options = {}) {
    return this.submit('get', `${this.endpoint}/v2/invoices-releasing`, null, { showResponseStatus: true, ...options });
  }

  replaceInvoice(payload, options = {}) {
    const data = handleDataInvoiceChange(payload);
    return this.submit('post', `${this.endpoint}/v2/replace-invoice`, data, { ...options });
  }

  editInfoInvoice(payload, options = {}) {
    const data = handleDataInvoiceChange(payload);
    return this.submit('post', `${this.endpoint}/v2/edit-info-invoice`, data, { ...options });
  }

  editProductInvoice(payload, options = {}) {
    const data = handleDataInvoiceChange(payload);
    return this.submit('post', `${this.endpoint}/v2/edit-product-invoice`, data, { ...options });
  }

  globalSearch(searchText, options = {}) {
    const data = { search_text: searchText };
    const promise = this.submit('post', `${this.endpoint}/v2/global-search`, data, { ...options });
    return new Promise((resolve, reject) => {
      promise.then(
        (res) => {
          resolve(res);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  exportExcelInconsistencyInvoices(payload, options = {}) {
    const data = {
      ...payload,
      start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
      end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
      origins: ['0', '1', '2', '3', '4'],
      transaction: 3,
    };
    const configs = {
      toast: {
        success: Vue.i18n.t('invoice.exportExcelSuccess'),
      },
      responseType: 'blob',
      ...options,
    };
    return new Promise((resolve, reject) => {
      this.submit('post', `${this.endpoint}/invoices/export-excel`, data, configs).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `report_${data.start_date}_${data.end_date}.xlsx`);
        document.body.appendChild(link);
        link.click();
        resolve(response);
      }).catch(e => reject(e));
    });
  }

  getPartnerInvoices({ page, limit, id }) {
    const requestUrl = `${this.endpoint}/partner/invoices?page=${page}&page_size=${limit}&id=${id}`;
    const promise = this.submit('get', requestUrl);
    return new Promise((resolve, reject) => {
      promise.then(
        (res) => {
          const result = roundedAmountDecorator(res);
          resolve(result);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  createInvoice(invoice) {
    const toast = {
      success: Vue.i18n.t('invoice.invoiceCreated'),
    };
    invoice.issued_date = DateTimeUtils.formatDate(
      invoice.issued_date,
      'YYYY-MM-DD',
    );
    invoice.paid_date =
      DateTimeUtils.formatDate(invoice.paid_date, 'YYYY-MM-DD') || null;
    return this.submit('post', `${this.endpoint}/create-invoice-pinvoice`, invoice, {
      toast,
    });
  }

  approveListInvoice(listInvoice) {
    const data = {
      ids: listInvoice,
    };
    const toast = {
      success: Vue.i18n.t('invoice.invoiceApproved'),
    };

    return this.submit('post', `${this.endpoint}/approval-invoices`, data, {
      toast,
    });
  }

  updateInvoice(invoice) {
    const toast = {
      success: Vue.i18n.t('invoice.invoiceUpdated'),
      error: Vue.i18n.t('invoice.invoiceUpdatedError'),
    };
    invoice.issued_date = DateTimeUtils.formatDate(
      invoice.issued_date,
      'YYYY-MM-DD',
    );
    invoice.paid_date =
      DateTimeUtils.formatDate(invoice.paid_date, 'YYYY-MM-DD') || null;
    roundedInvoiceAmount(invoice);
    return this.submit('post', `${this.endpoint}/update-invoice`, invoice, {
      toast,
    });
  }

  deleteInvoices(ids) {
    const toast = {
      success: Vue.i18n.t('invoice.invoiceDeleted'),
    };
    return this.submit(
      'post',
      `${this.endpoint}/delete-invoices`,
      { invoices: ids },
      { toast },
    );
  }

  getInvoiceFile(id) {
    return this.submit('post', `${this.endpoint}/get-invoice-file`, { id });
  }

  getSharedInvoiceFile(payload) {
    return this.submit(
      'post',
      `${this.endpoint}/get-invoice-file-client`,
      payload,
    );
  }

  moveToDirectory(payload) {
    const toast = {
      success: Vue.i18n.t('invoice.moveInvoiceSuccess'),
    };
    return this.submit(
      'post',
      `${this.endpoint}/move-invoices-to-dir`,
      payload,
      { toast },
    );
  }

  setPaidDate(payload) {
    const toast = {
      success: Vue.i18n.t('invoice.paidDateConfirm'),
    };
    return this.submit(
      'post',
      `${this.endpoint}/confirm-paid`,
      {
        ...payload,
        date_confirm: DateTimeUtils.formatDate(
          payload.date_confirm,
          'YYYY-MM-DD',
        ),
      },
      { toast },
    );
  }

  cloneInvoice(payload, options) {
    return this.submit(
      'post',
      `${this.endpoint}/take-invoices-from-vendor`,
      payload,
      options,
    );
  }

  setTag(payload) {
    const toast = {
      success: Vue.i18n.t('invoice.setTagSuccess'),
    };
    return this.submit(
      'post',
      `${this.endpoint}/set-tag-to-invoices`,
      payload,
      { toast },
    );
  }

  importExcel(file, uploadStatus) {
    const formData = new FormData();
    formData.append('file', file);
    const toast = {
      error: 'Import excel error',
    };
    const result = this.submit(
      'post',
      `${this.endpoint}/upload-invoices-by-excel`,
      formData,
      {
        toast,
        onUploadProgress(progressEvent) {
          uploadStatus.percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

    const parseInvoiceFn = (inv) => {
      inv.issued_date = DateTimeUtils.parseDate(inv.issued_date).toDate();
      inv.tag = [];
      inv.invoice_no = null;
      inv.template_code = null;
      inv.cqt_code = null;
      inv.paid_date = null;
      inv.received_date = null;
      inv.subscriber_no = null;
      inv.CCID = null;
      inv.buyer_address = null;
      inv.content = null;
      inv.parent_id = null;
      inv.company_id = null;
      inv.status = 'APPROVED';
      inv.id_pinvoice = null;
      inv.id_pvs = null;
      inv.einvoice_provider = null;
      inv.type = [];
      roundedInvoiceAmount(inv);
    };

    return new Promise((resolve, reject) => {
      result
        .then((res) => {
          if (res.invoices_already_exist) {
            res.invoices_already_exist.forEach(parseInvoiceFn);
          }
          if (res.invoices_uploaded_successfully && res.invoices_uploaded_successfully.length > 0) {
            res.invoices_uploaded_successfully.forEach((categorizedInvoice) => {
              categorizedInvoice.invoices.forEach(parseInvoiceFn);
            });
          }
          resolve(res);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  uploadVirtualInvoice(payload, uploadStatus) {
    uploadStatus.uploading = true;
    const formData = new FormData();
    Object.keys(payload).forEach((k) => {
      formData.append(k, payload[k]);
    });
    const toast = {
      error: 'Upload error',
    };
    return this.submit(
      'post',
      `${this.endpoint}/upload-virtual-invoice`,
      formData,
      {
        toast,
        onUploadProgress(progressEvent) {
          uploadStatus.percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  }

  /**
   * @returns {Promise}
   * @param invoiceFiles
   * @param uploadStatus
   */
  upload(invoiceFiles, uploadStatus) {
    uploadStatus.uploading = true;
    const formData = new FormData();
    formData.append('kind_of', 'false');
    formData.append('parent_id', 'null');
    invoiceFiles.forEach((invoice) => {
      formData.append('invoices', invoice);
    });
    const toast = {
      error: 'Upload error',
    };
    const result = this.submit(
      'post',
      `${this.endpoint}/upload-invoices`,
      formData,
      {
        toast,
        onUploadProgress(progressEvent) {
          uploadStatus.percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return new Promise((resolve, reject) => {
      result
        .then((res) => {
          res.invoices.forEach((inv) => {
            inv.issued_date = DateTimeUtils.parseDate(inv.issued_date).toDate();
            roundedInvoiceAmount(inv);
          });
          resolve(res);
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  searchTaxCode(taxCode) {
    return this.submit('get', `https://api-search.pvssolution.com/search_taxcode?taxcode=${taxCode}`);
  }

  checkValidCustomer(invoiceId) {
    return this.submit('post', `${this.endpoint}/check-valid-customer`, { invoice_id: invoiceId });
  }

  markInvoiceAsConsidered(invoiceIds, reason) {
    return this.submit('post', `${this.endpoint}/mark-considered`, { invoice_ids: invoiceIds, reason, consider_or_not: true });
  }

  getDataTaxDeduction(payload, options = {}) {
    return this.submit('post', `${this.endpoint}/v2/get-data-tax-deduction`, payload, options);
  }

  generateXMLTaxDeclaration(payload, options = { responseType: 'blob', showResponseHeaders: true }) {
    return this.submit('post', `${this.endpoint}/v2/invoices/generate-xml-tax-declaration`, payload, options);
  }
}

export default InvoiceProxy;



// WEBPACK FOOTER //
// ./src/store/proxies/invoice-proxy.js